import React, { useEffect, useState } from 'react'
import useManageOrder from '../order/useManageOrder'
import TableRow from './TableRow';
import MercariDashboardService from '../mercari/mercariAdminDashboard.js/mercariDashboardService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FormControl, InputLabel, Select, MenuItem, ListItemIcon, ListItemText, Checkbox, Divider } from '@mui/material';


const AddTracking = () => {

  const { authState } = useAuth();
  const [activeTab, setActiveTab] = useState('new'); // Default to 'new' orders

  const {
    orderGroup,
    updateTrackingJp,
    updateOrder,
    getNoTrackingOrder,
    updateOrderStatus,
    cancelOrder,
  } = useManageOrder();

  const [selectedWebsites, setSelectedWebsites] = useState([]);
  const [shipTypeFilter, setShipTypeFilter] = useState('all'); // all | air | ship
  const [sortPreOrderTop, setSortPreOrderTop] = useState(false);
  const uniqueWebsites = [...new Set(orderGroup.map(order => order.website).filter(Boolean))].sort();

  const handleWebsiteSelectChange = (e) => {
    const val = e.target.value;
    if (Array.isArray(val) && val.includes('__CLEAR_FILTER__')) {
      setSelectedWebsites([]); // Deselect all = show all websites (default)
    } else {
      setSelectedWebsites(val);
    }
  };

  // First page (new): unchanged. Other tabs: only show orders matching new flow status.
  const FOLLOW_TABS = ['follow_1', 'follow_2', 'follow_3', 'pending_cancel'];
  const filteredOrders = (() => {
    let orders = orderGroup || [];
    if (activeTab !== 'new' && FOLLOW_TABS.includes(activeTab)) {
      orders = orders.filter((order) => {
        const trackings = order.ShipmentStatusTrackings || [];
        const active = trackings.find((t) => t.statusDoneAt == null);
        return active && active.status === activeTab;
      });
    }
    if (selectedWebsites.length > 0) {
      orders = orders.filter((order) => selectedWebsites.includes(order.website));
    }
    if (shipTypeFilter !== 'all') {
      // presence of a hand-carry row = Air; absence = Ship (sea)
      orders = orders.filter((order) => shipTypeFilter === 'air' ? !!order.handCarry : !order.handCarry);
    }
    if (sortPreOrderTop) {
      orders = [...orders].sort((a, b) => {
        const aPre = a.isPre ? 1 : 0;
        const bPre = b.isPre ? 1 : 0;
        return bPre - aPre; // Pre-orders first
      });
    }
    return orders;
  })();


  const handleSave = async (order, mercariImage = null, notMercariBypass = false) => {
    const isMercari = order.website?.toLowerCase().includes('mercari');
    const imageToSend = isMercari && !notMercariBypass ? mercariImage : null;
    const orderToSend = notMercariBypass ? { ...order, notMercariBypass: 'true' } : order;
    const addResult = await updateTrackingJp(orderToSend, authState.user.id, imageToSend);
    if(addResult && addResult.success){
      toast.success('Update Order Successfully!!')
    } else {
      const errorMsg = addResult && addResult.message 
        ? addResult.message 
        : 'Failed to update order. Something went wrong.';
      toast.error(errorMsg)
    }
  };

  // ส่งตรวจ: the seller → inspection-centre number. It goes to the purchase, not to
  // orders.trackingJp — that field is kept for the second leg, the parcel that
  // comes on from the centre to our warehouse once the appraisal passes.
  const handleSaveAppraisal = async ({ orderId, trackingJp, images, notMercariBypass }) => {
    try {
      await MercariDashboardService.markAppraisalSent({ orderId, trackingJp, images, notMercariBypass });
      toast.success('บันทึกเลขพัสดุส่งตรวจแล้ว');
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'บันทึกเลขพัสดุส่งตรวจไม่สำเร็จ');
      return false;
    }
  };

  const handleSavePreDate = async (order) => {
    const addResult = await updateOrder(order, authState.user.id);
    if (addResult === true) {
      toast.success('Pre-Order Date updated successfully!');
      await getNoTrackingOrder(activeTab);
    } else {
      toast.error(addResult || 'Failed to update pre-order date.');
    }
  };

  // New flow: new → follow_1 → follow_2 → follow_3 → pending_cancel; pending_cancel (>5d) → follow_3 (ตามเรื่องอีกที)
  const handleStatusChange = async (order, currentTab) => {
    const statusFlow = {
      'new': 'follow_1',
      'follow_1': 'follow_2',
      'follow_2': 'follow_3',
      'follow_3': 'pending_cancel',
      'pending_cancel': 'follow_3', // ตามเรื่องอีกที
    };

    const currentStatus = currentTab || order.status;
    const nextStatus = statusFlow[currentStatus];

    if (nextStatus) {
      try {
        const updateResult = await updateOrderStatus({
          orderId: order.id,
          nextStatus,
          currentStatus,
        });
        if (updateResult) {
          toast.success(nextStatus === 'follow_3' && currentStatus === 'pending_cancel'
            ? 'ย้ายไปตามครั้งที่ 3 แล้ว (ตามเรื่องอีกที)'
            : `อัปเดตสถานะเรียบร้อย`);
          await getNoTrackingOrder(activeTab);
        }
      } catch (error) {
        toast.error('Failed to update order status');
      }
    } else {
      toast.warning('Cannot update status: Invalid current status');
    }
  };

  // Handle cancel order
  const handleCancel = async (order, currentTab) => {
    if (window.confirm('ยืนยันยกเลิกรายการนี้?')) {
      try {
        const result = await cancelOrder({
          orderId: order.id,
          currentStatus: currentTab || order.status,
          adminId: authState.user.id
        });
        if (result) {
          toast.success('ยกเลิกรายการเรียบร้อย');
          await getNoTrackingOrder(activeTab);
        }
      } catch (error) {
        toast.error('ไม่สามารถยกเลิกรายการได้');
      }
    }
  };

  // Tab configuration: 5/7/9 days for follow 1/2/3; รอยกเลิก >5 days → ตามเรื่องอีกที
  const tabs = [
    { id: 'new', label: 'รายการใหม่ (5 วัน)' },
    { id: 'follow_1', label: 'ตามครั้งที่ 1 (5 วัน)' },
    { id: 'follow_2', label: 'ตามครั้งที่ 2 (7 วัน)' },
    { id: 'follow_3', label: 'ตามครั้งที่ 3 (9 วัน)' },
    { id: 'pending_cancel', label: 'รอยกเลิก (เกิน 5 วัน → ตามเรื่องอีกที)' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Modify the API call to include the active tab
        const response = await getNoTrackingOrder(activeTab);
        if (!response) {
          //console.log("error")
        }
      } catch (error) {
        console.log("Error fetching data:", error);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when activeTab changes only
  }, [activeTab]);

  return (
    <div>
      {/* Tabs */}
      <div className="flex mb-4 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`px-4 py-2 mr-2 ${
              activeTab === tab.id
                ? 'border-b-2 border-blue-500 text-blue-500'
                : 'text-gray-500'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <FormControl 
          size="small" 
          variant="outlined" 
          sx={{ 
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#eff6ff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              '& fieldset': { 
                borderColor: '#3b82f6', 
                borderWidth: 2,
                borderRadius: 1
              },
              '&:hover fieldset': { 
                borderColor: '#2563eb',
                backgroundColor: '#dbeafe'
              },
              '&.Mui-focused fieldset': { 
                borderColor: '#1d4ed8', 
                borderWidth: 2,
                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.25)'
              },
              '& .MuiSelect-icon': { color: '#1d4ed8' }
            },
            '& .MuiInputLabel-root.Mui-focused': { color: '#1d4ed8' }
          }}
        >
          <InputLabel id="website-filter-label">Filter by Website</InputLabel>
          <Select
            labelId="website-filter-label"
            multiple
            value={selectedWebsites}
            onChange={handleWebsiteSelectChange}
            label="Filter by Website"
            renderValue={(selected) => selected.length === 0 ? 'All Websites' : `${selected.length} selected: ${selected.join(', ')}`}
            MenuProps={{
              PaperProps: {
                sx: { mt: 1.5, boxShadow: 3 }
              }
            }}
          >
            <MenuItem value="__CLEAR_FILTER__" dense sx={{ fontStyle: 'italic' }}>
              <ListItemText primary="Clear filter (show all)" />
            </MenuItem>
            <Divider />
            {uniqueWebsites.map((website) => (
              <MenuItem key={website} value={website}>
                <ListItemIcon>
                  <Checkbox checked={selectedWebsites.indexOf(website) > -1} size="small" />
                </ListItemIcon>
                <ListItemText primary={website} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" sx={{ minWidth: 140 }}>
          <InputLabel id="shiptype-filter-label">Air / Ship</InputLabel>
          <Select
            labelId="shiptype-filter-label"
            value={shipTypeFilter}
            onChange={(e) => setShipTypeFilter(e.target.value)}
            label="Air / Ship"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="air">✈️ Air</MenuItem>
            <MenuItem value="ship">🚢 Ship</MenuItem>
          </Select>
        </FormControl>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sortPreOrderTop}
            onChange={(e) => setSortPreOrderTop(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="font-medium">Sort pre-order to top</span>
        </label>
      </div>

      <table className='min-w-full border-collapse border border-gray-300'>
        <thead>
        <tr className="bg-gray-100">
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Order Date</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>สั่งซื้อโดย</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Days Passed</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Contact</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Smile Name</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Link</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Website</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Type</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Tracking</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Pre-Order Date</td>
          <td className='p-4 border-b border-gray-200 text-left text-sm font-semibold'>Actions</td>
        </tr>
        </thead>
        <tbody>
        {filteredOrders.length && filteredOrders.map((order,index) => (
          <TableRow 
            key={order.id} 
            order={order} 
            handleSave={handleSave}
            handleSaveAppraisal={handleSaveAppraisal}
            handleSavePreDate={handleSavePreDate}
            handleStatusChange={handleStatusChange}
            handleCancel={handleCancel}
            currentTab={activeTab}
          />
        ))}
        </tbody>
      </table>
    </div>
  )
}

export default AddTracking